import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, FileText } from "lucide-react";
import { Seo, breadcrumbSchema } from "../components/Seo";
import { ServiceCTA } from "../components/ServiceCTA";

const CustomPage = () => {
  const { slug }               = useParams();
  const [page, setPage]        = useState(null);
  const [loading, setLoading]  = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    fetch(`/api/custom-pages.php?slug=${encodeURIComponent(slug)}`)
      .then((r) => {
        if (r.status === 404) { setNotFound(true); return null; }
        return r.json();
      })
      .then((d) => { if (d?.page) setPage(d.page); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">
        Loading…
      </div>
    );
  }

  if (notFound || !page) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-6 py-20">
        <FileText className="w-12 h-12 text-slate-300 mb-4" />
        <h1 className="text-2xl font-bold text-navy mb-2">Page not found</h1>
        <p className="text-slate-500 mb-6">This page doesn't exist or has been removed.</p>
        <Link to="/" className="bg-navy text-white px-5 py-3 rounded-xl font-semibold hover:bg-navy-light transition-colors">
          Back to Home
        </Link>
      </div>
    );
  }

  const heroTitle = page.heroHeading || page.title;

  return (
    <>
      <Seo
        title={page.metaTitle || page.title}
        description={page.metaDescription || ""}
        ogTitle={page.metaTitle || page.title}
        ogDescription={page.metaDescription || ""}
        ogImage={page.heroImage || undefined}
        path={`/p/${page.slug}`}
        schema={[
          breadcrumbSchema([{ name: page.title, path: `/p/${page.slug}` }]),
        ]}
      />

      {/* Hero */}
      <section className="relative pt-28 pb-14 md:pt-40 md:pb-20 bg-navy overflow-hidden">
        {page.heroImage && (
          <div className="absolute inset-0">
            <img src={page.heroImage} alt="" className="w-full h-full object-cover opacity-20" />
            <div className="absolute inset-0 bg-navy/80" />
          </div>
        )}
        <div className="relative z-10 container mx-auto px-6 md:px-12 lg:px-20 max-w-4xl">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            {page.heroEyebrow && (
              <span className="inline-block bg-gold/20 text-gold px-4 py-2 rounded-full text-sm font-semibold mb-5 uppercase tracking-wide">
                {page.heroEyebrow}
              </span>
            )}
            <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4">
              {heroTitle}
            </h1>
            {page.heroSubheading && (
              <p className="text-slate-300 text-lg leading-relaxed max-w-2xl">
                {page.heroSubheading}
              </p>
            )}
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 md:py-20 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-20">
          <div className="max-w-3xl mx-auto">
            <div
              className="prose prose-lg prose-slate max-w-none
                prose-headings:font-heading prose-headings:text-navy
                prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                prose-p:text-slate-700 prose-p:leading-relaxed
                prose-a:text-teal prose-a:no-underline hover:prose-a:text-navy
                prose-strong:text-navy
                prose-li:text-slate-700
                prose-img:rounded-2xl prose-img:shadow-sm"
              dangerouslySetInnerHTML={{ __html: page.content }}
            />

            <div className="mt-10 pt-8 border-t border-slate-200">
              <Link to="/" className="inline-flex items-center gap-2 text-teal hover:text-navy font-semibold transition-colors text-sm">
                <ArrowLeft className="w-4 h-4" /> Back to home
              </Link>
            </div>
          </div>
        </div>
      </section>

      <ServiceCTA
        title="Need a quote for your London clean?"
        description="Fixed price, 7-day re-clean guarantee, all 32 London boroughs covered."
        variant="navy"
      />
    </>
  );
};

export default CustomPage;
